<!-- Generated by Copilot -->
<template>
  <div class="duration-histogram">
    <Bar
      v-if="chartData.datasets.length > 0"
      :data="chartData"
      :options="chartOptions"
    />
    <v-alert v-else type="info" variant="outlined" density="compact">
      No duration data available for histogram.
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js';
import type { RequestStats } from '@/esri/services/TempoDataService';

// Register Chart.js components
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

interface Props {
  stats: RequestStats[] | null;
  binCount?: number;
  title?: string;
}

const props = withDefaults(defineProps<Props>(), {
  binCount: 10,
  title: 'Request Duration Distribution'
});

// Determine if we should use seconds (when most data is > 100ms)
const useSeconds = computed(() => {
  if (!props.stats || props.stats.length === 0) return false;
  const durations = props.stats.map(s => s.duration);
  const countOver100ms = durations.filter(d => d > 100).length;
  return countOver100ms > durations.length / 2; // More than half are over 100ms
});

// Get the time unit label
const timeUnit = computed(() => useSeconds.value ? 's' : 'ms');

// Calculate histogram bins from duration data
const histogramData = computed(() => {
  if (!props.stats || props.stats.length === 0) {
    return { labels: [], successCounts: [], errorCounts: [] };
  }

  const durations = props.stats.map(s => s.duration);
  const minDuration = Math.min(...durations);
  const maxDuration = Math.max(...durations);
  
  // Convert to seconds if needed
  const scale = useSeconds.value ? 1000 : 1;
  const scaledMin = minDuration / scale;
  const scaledMax = maxDuration / scale;
  
  // Handle edge case where all durations are the same
  if (minDuration === maxDuration) {
    const label = useSeconds.value 
      ? `${(minDuration / 1000).toFixed(2)}` 
      : `${Math.round(minDuration)}`;
    const successCount = props.stats.filter(s => s.status === 'success').length;
    const errorCount = props.stats.filter(s => s.status === 'error').length;
    return {
      labels: [label],
      successCounts: [successCount],
      errorCounts: [errorCount]
    };
  }

  // Create bins
  const binWidth = (scaledMax - scaledMin) / props.binCount;
  const labels: string[] = [];
  const successCounts: number[] = [];
  const errorCounts: number[] = [];

  // Format value based on scale
  const formatValue = (val: number) => {
    if (useSeconds.value) {
      // For seconds, use appropriate decimal places
      if (val < 0.1) return val.toFixed(3);
      if (val < 1) return val.toFixed(2);
      return val.toFixed(1);
    } else {
      // For milliseconds
      return Math.round(val).toString();
    }
  };

  for (let i = 0; i < props.binCount; i++) {
    const binStart = scaledMin + i * binWidth;
    const binEnd = scaledMin + (i + 1) * binWidth;
    
    labels.push(`${formatValue(binStart)}-${formatValue(binEnd)}`);
    
    // Count requests in this bin (compare in original ms units)
    const binStartMs = binStart * scale;
    const binEndMs = binEnd * scale;
    const binStats = props.stats.filter(s => {
      if (i === props.binCount - 1) {
        // Last bin includes the max value
        return s.duration >= binStartMs && s.duration <= binEndMs;
      }
      return s.duration >= binStartMs && s.duration < binEndMs;
    });
    
    successCounts.push(binStats.filter(s => s.status === 'success').length);
    errorCounts.push(binStats.filter(s => s.status === 'error').length);
  }

  return { labels, successCounts, errorCounts };
});

// Chart data configuration
const chartData = computed(() => ({
  labels: histogramData.value.labels,
  datasets: [
    {
      label: 'Successful Requests',
      backgroundColor: 'rgba(76, 175, 80, 0.7)',
      borderColor: 'rgb(76, 175, 80)',
      borderWidth: 1,
      data: histogramData.value.successCounts
    },
    {
      label: 'Failed Requests',
      backgroundColor: 'rgba(244, 67, 54, 0.7)',
      borderColor: 'rgb(244, 67, 54)',
      borderWidth: 1,
      data: histogramData.value.errorCounts
    }
  ]
}));

// Chart options
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: props.title
    },
    tooltip: {
      callbacks: {
        title: (context: { label: string }[]) => {
          return `Duration: ${context[0].label} ${timeUnit.value}`;
        }
      }
    },
    legend: {
      position: 'top' as const
    }
  },
  scales: {
    x: {
      title: {
        display: true,
        text: `Duration (${timeUnit.value})`
      },
      stacked: true
    },
    y: {
      title: {
        display: true,
        text: 'Number of Requests'
      },
      stacked: true,
      beginAtZero: true,
      ticks: {
        stepSize: 1
      }
    }
  }
}));
</script>

<style scoped>
.duration-histogram {
  width: 100%;
  min-height: 250px;
  height: 300px;
}
</style>
